import { Theme } from '@emotion/react';
import {
  NamedColorOrGrayValue,
  resolveThemeValueFromNamedColor,
  useTheme,
} from '@hooks/useTheme';
import { keys } from '@utils/keys';
import { intersection, last, omit, pickBy } from 'lodash-es';
import { FC, HTMLProps } from 'react';
import { PRIVATE_colors } from '../Theme/colors';

type ColorMap = { [k in keyof Theme['colors']]?: Maybe<boolean | number> };

interface Props extends ColorMap, HTMLProps<HTMLSpanElement> {
  as?: 'div' | 'span' | 'strong' | 'em' | 'label' | 'code';
  color?: NamedColorOrGrayValue;
  /** Adds asterisk to display */
  required?: boolean;
}

export const Text: FC<Props> = ({
  as: asProp = 'span',
  children,
  color,
  required,
  ...others
}) => {
  const theme = useTheme();
  // We generate the possibleColorKeys from the import, and not the derived value of useTheme() because this component can sometimes be rendered without the themeprovider present - in the case of Table CSV rendering
  let resolvedColor: string | undefined = undefined;
  const possibleColorKeys = Object.keys(PRIVATE_colors);
  const rest = omit(others, possibleColorKeys);
  if (color) {
    resolvedColor = resolveThemeValueFromNamedColor(theme, color);
  } else {
    const otherKeysWithNullishValuesRemoved = keys(pickBy(others)) as string[];
    const matchingColorKeys = intersection(
      otherKeysWithNullishValuesRemoved,
      possibleColorKeys,
    );
    const lastMatchingKey = last(matchingColorKeys) || '';
    resolvedColor = resolveThemeValueFromNamedColor(
      theme,
      lastMatchingKey as NamedColorOrGrayValue,
    );
  }
  const El = asProp as anyOk;
  return (
    <El css={{ color: resolvedColor }} {...rest}>
      {children}
      {required && <span css={{ color: theme.text.error }}>&nbsp;*</span>}
    </El>
  );
};
